import React, { ChangeEvent, useState } from 'react';

import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { EditHistoryHeadcells, EditHistoryItem } from 'views/EditHistory/EditHistoryHeadcells';
import RevisionHistoryItemDetailedView from 'views/EditHistory/EditHistoryItemDetail';
import { getComparator, Order, stableSort } from 'utils/dataTableUtils';
import { getUniquePropertiesFromSubArray, getUniquePropertyFromArray, valueExistsInObject } from 'utils/revisionUtil';
import EnhancedTableHead from 'components/DataTable/EnhancedTableHead';
import { TextFilter } from 'components/Common/TextFilter';

const EditHistoryTable = ({ rawData }: { rawData: EditHistoryItem[] }) => {
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState('editedAt');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [rowsFiltered, setRowsFiltered] = useState<EditHistoryItem[]>([]);

  const rows: EditHistoryItem[] = rawData
    ?.map((data: EditHistoryItem) => {
      return {
        ...data,
        details: [
          ...data.details.map((detail) => {
            return { ...detail, profileTab: 'Core data' };
          }),
        ],
      };
    })
    .map((data: EditHistoryItem) => {
      return {
        ...data,
        profileTabs: getUniquePropertyFromArray(data, 'details', 'profileTab') as string[],
        sectionsUpdated: getUniquePropertyFromArray(data, 'details', 'section') as string[],
      };
    });

  const [searchWord, setSearchWord] = useState<string>('');
  const searchedFilteredRows = rowsFiltered.filter((row) => valueExistsInObject(row, searchWord));
  const uniqueProfileTabs = getUniquePropertiesFromSubArray(rows, 'details', 'profileTab');
  const uniqueSections = getUniquePropertiesFromSubArray(rows, 'details', 'section');

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSort = (selectedFilters: { [key: string]: string }[]) => {
    const rowsMatchingFilters = selectedFilters
      ? rows?.filter((row: any) =>
          selectedFilters.some((selectedFilter) =>
            Object.keys(selectedFilter).some((key) => {
              return Array.isArray(row[key])
                ? row[key].includes(selectedFilter[key]) || (row[key] as any) === selectedFilter[key]
                : row[key] === selectedFilter[key];
            }),
          ),
        )
      : [];
    selectedFilters.length > 0 ? setRowsFiltered(rowsMatchingFilters) : setRowsFiltered([]);
  };

  return (
    <>
      <Grid container>
        <Grid item xs={3}>
          <TextFilter
            filterName="Search"
            placeholderText=""
            dispatchAction={(val: string) => {
              setSearchWord(val);
            }}
          />
        </Grid>
      </Grid>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="Revision history table">
          <EnhancedTableHead
            data={rows}
            headCells={EditHistoryHeadcells(uniqueProfileTabs, uniqueSections)}
            order={order}
            setOrderBy={setOrderBy}
            setOrder={setOrder}
            orderBy={orderBy}
            handleSort={handleSort}
            handleSearch={(value) => setSearchWord(value)}
          />
          <TableBody>
            {searchedFilteredRows?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography>No revision history found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              stableSort(searchedFilteredRows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((rowFiltered: EditHistoryItem, index: number) => (
                  <RevisionHistoryItemDetailedView row={rowFiltered} key={index} />
                ))
            )}
            {searchedFilteredRows?.length > 10 && (
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 20, 50]}
                  count={searchedFilteredRows?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Entries per page"
                />{' '}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default EditHistoryTable;
